import { z } from 'zod'

import { baseRegistrySchema } from '@/registry'
import {
  docVersionSchema,
  entitySchema,
  namespaceSchema,
  nanoidSchema,
  slugSchema,
} from '@/utility'

/**
 * Supported SDK languages
 *
 * Ruby and Unity-integrated C# will be added later
 */
export enum SdkLanguage {
  Typescript = 'typescript',
  Python = 'python',
  CSharp = 'csharp',
  Java = 'java',
  Ruby = 'ruby',
  Php = 'php',
  Go = 'go',
}

export const SdkLanguageLabels: Record<SdkLanguage, string> = {
  [SdkLanguage.Typescript]: 'Typescript',
  [SdkLanguage.Python]: 'Python',
  [SdkLanguage.CSharp]: 'C#',
  [SdkLanguage.Java]: 'Java',
  [SdkLanguage.Ruby]: 'Ruby',
  [SdkLanguage.Php]: 'PHP',
  [SdkLanguage.Go]: 'Go',
}

/**
 * SDK Version schema
 */
const sdkVersionSchema = z
  .object({
    version: docVersionSchema,
    apiVersion: docVersionSchema,
    activeSync: z
      .object({
        repository: z.string(),
        pullRequest: z.number(),
        commitHash: z.string(),
      })
      .optional(),
    activeBuild: nanoidSchema.optional(),
  })
  .meta({ id: 'sdk-version' })

export type SdkVersion = z.infer<typeof sdkVersionSchema>

export const sdkPreviewSchema = z
  .object({
    code: z.string(),
    language: z.string(),
    method: z.string().optional(),
    operationId: z.string(),
    path: z.string(),
  })
  .meta({ id: 'sdk-preview' })

export type SdkPreview = z.infer<typeof sdkPreviewSchema>

export const sdkRepositorySchema = z
  .object({
    linkedBy: z.string(),
    id: z.number(),
    name: z.string().min(2),
    baseBranch: z.string().min(1).default('main'),
  })
  .meta({ id: 'sdk-repository' })

export type SdkRepository = z.infer<typeof sdkRepositorySchema>

/**
 * SDK schema
 */
export const sdkSchema = baseRegistrySchema
  .extend({
    /**
     * Non-optional API uid as schemas
     * only support registry APIs at the
     * moment.
     */
    apiUid: nanoidSchema,
    /** Linked GitHub repository */
    repository: sdkRepositorySchema.nullable().default(null),
    /**
     * Current latest revision
     * for SDK. Can be selected
     * by user.
     */
    currentVersion: nanoidSchema,
    /** All versions available for SDK. */
    versions: sdkVersionSchema.array().default([]),
    /** 1 Language = 1 SDK */
    language: z.enum(SdkLanguage),
    config: z.string(),
  })
  .meta({ id: 'sdk' })

export type Sdk = z.infer<typeof sdkSchema>

export const sdkBuildStatusSchema = z
  .enum(['pending', 'error', 'generated'])
  .meta({ id: 'sdk-build-status' })

export type SdkBuildStatus = z.infer<typeof sdkBuildStatusSchema>

export const sdkGitStatusSchema = z
  .enum(['pending', 'error', 'synced'])
  .meta({ id: 'sdk-git-status' })

export type SdkGitStatus = z.infer<typeof sdkGitStatusSchema>

export const sdkBuildApiSchema = z
  .object({
    uid: nanoidSchema,
    namespace: namespaceSchema,
    version: docVersionSchema,
    slug: slugSchema,
    upgraded: z.boolean().default(false),
  })
  .meta({ id: 'sdk-build-api' })

export type SdkBuildApi = z.infer<typeof sdkBuildApiSchema>

export const sdkbuildSdkSchema = z
  .object({
    uid: nanoidSchema,
    namespace: namespaceSchema,
    version: docVersionSchema,
    slug: slugSchema,
  })
  .meta({ id: 'sdk-build-sdk' })

export type SdkBuildSdk = z.infer<typeof sdkbuildSdkSchema>

export const sdkBuildGitSchema = z
  .object({
    pullRequest: z.number(),
    commitHash: z.string(),
  })
  .optional()
  .meta({ id: 'sdk-build-git' })

export const sdkBuildSchema = entitySchema
  .extend({
    teamUid: nanoidSchema,
    /** Build status */
    buildStatus: sdkBuildStatusSchema,
    /** Additional build message  */
    buildMessage: z.string().optional(),
    /** Git sync status */
    gitStatus: sdkGitStatusSchema.optional(),
    /** Additional git message */
    gitMessage: z.string().optional(),
    /** SDK language to generate */
    language: z.enum(SdkLanguage),
    /** SDK metadata on generation */
    sdk: sdkbuildSdkSchema,
    /** Original API used to generate SDK */
    api: sdkBuildApiSchema,
    /** Custom SDK config */
    config: z.string(),
    /** Linked git repository name */
    repository: z.string().nullable().optional(),
    /** Git sync commit and PR */
    gitSync: sdkBuildGitSchema,
    /** Extended generation logs */
    generationLog: z.string().optional(),
    /** Should auto set active sdk version */
    autoBump: z.boolean().default(false),
  })
  .meta({ id: 'sdk-build' })

export type SdkBuild = z.infer<typeof sdkBuildSchema>

export const sdkCodeSampleSchema = z
  .object({
    lang: z.string(),
    label: z.string(),
    source: z.string(),
  })
  .meta({ id: 'sdk-code-sample' })

export type SdkCodeSample = z.infer<typeof sdkCodeSampleSchema>
